import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';

// Keyboard navigation for a search-bar-over-results list (combobox pattern):
// ArrowDown / ArrowUp walk the rows while focus stays in the input, Enter
// activates the highlighted row, Escape backs out. Every web search surface
// shares this so the keys mean the same thing in the ⌘K modal, the map
// search overlay, the destination picker, and the booking title typeahead.
//
// The rows themselves stay owned by the caller — this only tracks which one
// is active, scrolls it into view, and translates keys into an activate call.
// Pass a flat index space: if a surface renders several sections (catalog,
// then Google, then a custom row), concatenate them in render order and give
// `count` the total.

export interface ListNavOptions {
  /** Rows currently rendered, in the flat order the arrows should walk. */
  count: number;
  /** Fired on Enter over the active row. */
  onActivate: (index: number) => void;
  /** Fired on Escape. Omit when the surface already closes on Escape. */
  onEscape?: () => void;
  /**
   * Row highlighted on first render and after `resetKey` changes.
   *   0  — top hit pre-selected, so Enter opens it without arrowing first.
   *        Right for overlay searches whose only job is picking a result.
   *  -1  — nothing selected, so the first ArrowDown is what moves off the
   *        search bar and a bare Enter falls through to the form. Right for
   *        typeaheads where the typed text is itself a valid answer.
   */
  initial?: number;
  /** Selection resets to `initial` whenever this changes — pass the query. */
  resetKey?: unknown;
}

export interface ListNav<T extends HTMLElement> {
  /** Active row, or -1 for "none" (focus is still on the search bar). */
  index: number;
  setIndex: (i: number) => void;
  /** Attach to the input (or any ancestor the keys bubble through). */
  onKeyDown: (e: ReactKeyboardEvent) => void;
  /** Attach to the scroll container that holds the rows. */
  listRef: RefObject<T>;
  /** Spread onto each row: marks the active one and tracks hover. */
  rowProps: (i: number) => {
    'data-listnav-active'?: 'true';
    'aria-selected': boolean;
    onMouseEnter: () => void;
  };
}

export function useListNav<T extends HTMLElement = HTMLDivElement>({
  count,
  onActivate,
  onEscape,
  initial = 0,
  resetKey,
}: ListNavOptions): ListNav<T> {
  const [index, setIndex] = useState(initial);
  const listRef = useRef<T>(null);

  // Reset on a new query. Also clamp when the result set shrinks under the
  // cursor (debounced Google results landing can drop rows mid-arrow).
  useEffect(() => {
    setIndex(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  useEffect(() => {
    setIndex((i) => (i >= count ? (count === 0 ? initial : count - 1) : i));
  }, [count, initial]);

  // Keep the highlighted row visible as the arrows move past the fold.
  useEffect(() => {
    if (index < 0) return;
    listRef.current
      ?.querySelector('[data-listnav-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [index, count]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        if (!count) return;
        e.preventDefault();
        setIndex((i) => (i + 1 >= count ? 0 : i + 1));
      } else if (e.key === 'ArrowUp') {
        if (!count) return;
        e.preventDefault();
        // From "nothing selected", Up wraps to the last row.
        setIndex((i) => (i <= 0 ? count - 1 : i - 1));
      } else if (e.key === 'Enter') {
        if (index < 0 || index >= count) return;
        e.preventDefault();
        onActivate(index);
      } else if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
      }
    },
    [count, index, onActivate, onEscape],
  );

  const rowProps = useCallback(
    (i: number) => ({
      ...(i === index ? { 'data-listnav-active': 'true' as const } : {}),
      'aria-selected': i === index,
      onMouseEnter: () => setIndex(i),
    }),
    [index],
  );

  return { index, setIndex, onKeyDown, listRef, rowProps };
}
